import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';

// ============================================
// ROLES
// ============================================

/**
 * Obtener todos los roles
 * @param {Object} filters - Filtros opcionales {id_institucion}
 */
export const getRoles = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.id_institucion) {
      params.append('id_institucion', filters.id_institucion);
    }
    
    const response = await axios.get(
      `${API_BASE_URL}/catalogos/roles/`,
      { params }
    );
    return response.data;
  } catch (error) {
    console.error('Error al obtener roles:', error);
    throw error;
  }
};

/**
 * Obtener un rol por ID
 */
export const getRolById = async (id) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/catalogos/roles/${id}/`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener rol:', error);
    throw error;
  }
};

/**
 * Crear un nuevo rol
 * @param {Object} rolData - Datos del rol {nombre_rol, descripcion, id_institucion, ambito_visualizacion, nivel_acceso}
 */
export const createRol = async (rolData) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/catalogos/roles/`,
      rolData
    );
    return response.data;
  } catch (error) {
    console.error('Error al crear rol:', error);
    throw error;
  }
};

/**
 * Actualizar un rol existente
 */
export const updateRol = async (id, rolData) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/catalogos/roles/${id}/`,
      rolData
    );
    return response.data;
  } catch (error) {
    console.error('Error al actualizar rol:', error);
    throw error;
  }
};

/**
 * Eliminar un rol
 */
export const deleteRol = async (id) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/catalogos/roles/${id}/`);
    return response.data;
  } catch (error) {
    console.error('Error al eliminar rol:', error);
    throw error;
  }
};

/**
 * Obtener permisos de un rol específico
 */
export const getPermisosByRol = async (idRol) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/catalogos/roles/${idRol}/permisos/`
    );
    return response.data;
  } catch (error) {
    console.error('Error al obtener permisos del rol:', error);
    throw error;
  }
};

// ============================================
// PERMISOS
// ============================================

/**
 * Obtener todos los permisos
 * @param {Object} filters - Filtros opcionales {id_rol}
 */
export const getPermisos = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.id_rol) {
      params.append('id_rol', filters.id_rol);
    }
    
    const response = await axios.get(
      `${API_BASE_URL}/sistema/permisos/`,
      { params }
    );
    return response.data;
  } catch (error) {
    console.error('Error al obtener permisos:', error);
    throw error;
  }
};

/**
 * Crear/actualizar permisos en bulk para un rol
 * @param {Object} data - {id_rol, permisos: [{modulo, puede_crear, puede_leer, puede_actualizar, puede_eliminar}]}
 */
export const savePermisosBulk = async (data) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/sistema/permisos/bulk-create/`,
      data
    );
    return response.data;
  } catch (error) {
    console.error('Error al guardar permisos:', error);
    throw error;
  }
};

/**
 * Crear un permiso individual
 */
export const createPermiso = async (permisoData) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/sistema/permisos/`,
      permisoData
    );
    return response.data;
  } catch (error) {
    console.error('Error al crear permiso:', error);
    throw error;
  }
};

/**
 * Actualizar un permiso
 */
export const updatePermiso = async (id, permisoData) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/sistema/permisos/${id}/`,
      permisoData
    );
    return response.data;
  } catch (error) {
    console.error('Error al actualizar permiso:', error);
    throw error;
  }
};

/**
 * Eliminar un permiso
 */
export const deletePermiso = async (id) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/sistema/permisos/${id}/`);
    return response.data;
  } catch (error) {
    console.error('Error al eliminar permiso:', error);
    throw error;
  }
};

// ============================================
// UTILIDADES
// ============================================

/**
 * Obtener lista de instituciones para dropdown
 */
export const getInstituciones = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/catalogos/instituciones/`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener instituciones:', error);
    throw error;
  }
};

/**
 * Obtener lista de campus para asignación de roles
 */
export const getCampusForRoles = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/catalogos/campus/select/`);
    // Mapear respuesta a formato esperado {id, nombre}
    if (Array.isArray(response.data) && response.data.length > 0) {
      return response.data.map(c => ({
        id: c.id_campus,
        nombre: c.nombre_campus || c.nombre || 'Sin nombre'
      }));
    }
    return [];
  } catch (error) {
    console.error('Error al obtener campus:', error);
    // Si falla el endpoint select, intentar con el listado principal
    try {
      const response = await axios.get(`${API_BASE_URL}/catalogos/campus/`);
      // Mapear respuesta a formato esperado {id, nombre}
      if (Array.isArray(response.data) && response.data.length > 0) {
        return response.data.map(c => ({
          id: c.id_campus,
          nombre: c.nombre_campus || c.nombre || 'Sin nombre'
        }));
      }
      return [];
    } catch (fallbackError) {
      console.error('Error al obtener campus (fallback):', fallbackError);
      return [];
    }
  }
};

/**
 * Mapear permisos de formato backend a formato frontend
 */
export const mapPermisosToFrontend = (permisos) => {
  const permisosMap = {};
  
  permisos.forEach(permiso => {
    permisosMap[permiso.modulo] = {
      id_permiso: permiso.id_permiso,
      puede_crear: permiso.puede_crear,
      puede_leer: permiso.puede_leer,
      puede_actualizar: permiso.puede_actualizar,
      puede_eliminar: permiso.puede_eliminar,
      puede_aprobar: permiso.puede_aprobar
    };
  });
  
  return permisosMap;
};

/**
 * Mapear permisos de formato frontend a formato backend
 */
export const mapPermisosToBackend = (permisosMap) => {
  const permisos = [];
  
  Object.keys(permisosMap).forEach(modulo => {
    const permisoData = permisosMap[modulo];
    permisos.push({
      modulo: modulo,
      puede_crear: permisoData.puede_crear || false,
      puede_leer: permisoData.puede_leer || false,
      puede_actualizar: permisoData.puede_actualizar || false,
      puede_eliminar: permisoData.puede_eliminar || false,
      puede_aprobar: permisoData.puede_aprobar || false
    });
  });
  
  return permisos;
};
